import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard, faChevronLeft, faChevronRight, faEllipsisV, faGlobe, faSignOutAlt, faTableList, faUserCircle, faPlusSquare } from '@fortawesome/free-solid-svg-icons'
import { faBattleNet } from '@fortawesome/free-brands-svg-icons';
import BrandLogo from './BrandLogo';
import { NavLink, Link } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../../config';
import { UseAuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Main(props) {
  const { i18n } = useTranslation();
  const { instance, accounts } = useMsal();
  const [sideEnable, setSideEnable] = useState(true);
  const [mobileEnable, setMobileEnable] = useState(false);
  const { setToken } = UseAuthContext();
  const refreshTimeoutRef = useRef(null);

  useEffect(() => {
    if (accounts.length === 0) {
      return;
    }

    // Acquires a fresh token (using MSAL's cache/refresh under the hood) and
    // schedules the next silent refresh shortly before this token expires, so
    // the token stays valid even if the browser tab is left idle for a long time.
    const refreshToken = () => {
      instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] }).then((response) => {
        setToken(response.accessToken);

        const expiresOn = response.expiresOn ? response.expiresOn.getTime() : Date.now() + 60 * 60 * 1000;
        // Refresh 5 minutes before actual expiry, never sooner than 30s from now.
        const delay = Math.max(expiresOn - Date.now() - 5 * 60 * 1000, 30 * 1000);

        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = setTimeout(refreshToken, delay);
      }).catch(() => {
        instance.acquireTokenPopup({ ...loginRequest, account: accounts[0] }).then((response) => {
          setToken(response.accessToken);

          const expiresOn = response.expiresOn ? response.expiresOn.getTime() : Date.now() + 60 * 60 * 1000;
          const delay = Math.max(expiresOn - Date.now() - 5 * 60 * 1000, 30 * 1000);

          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = setTimeout(refreshToken, delay);
        });
      });
    };

    refreshToken();

    // Also refresh right away whenever the tab regains focus/visibility after
    // being backgrounded/idle, in case the scheduled timer was throttled by
    // the browser while the tab was hidden.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(refreshTimeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accounts, instance, setToken])

  // Safety net: if a request still fails with 401 (e.g. token expired right
  // between refreshes), silently acquire a new token and retry the request
  // once, instead of surfacing the failure to the user.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && accounts.length > 0 && !originalRequest._retry) {
          originalRequest._retry = true;
          return instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] }).then((response) => {
            setToken(response.accessToken);
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
            return axios(originalRequest);
          }).catch(() => Promise.reject(error));
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, [accounts, instance, setToken])

  const logoutHandler = (instance) => {
    instance.logoutPopup().catch(e => {
      console.error(e);
    });
  }

  const handleLang = (val) => {
    if (val.includes("en")) {
      i18n.changeLanguage('dk');
    } else {
      i18n.changeLanguage('en');
    }
  }

  return (
    <div className="wrapper">
      <div className={mobileEnable ? "body-overlay show-nav" : "body-overlay"} onClick={() => setMobileEnable(false)} />
      <nav id="sidebar" className={mobileEnable ? "show-nav" : sideEnable ? "" : "active"}>
        <div className="sidebar-header">
          <h3><BrandLogo /></h3>
        </div>
        <ul className="list-unstyled components">
          <li className="dropdown d-lg-none d-md-block d-xl-none d-sm-block active small-screen navbar-display">
            <a href="#homeSubmenu0" data-toggle="collapse" aria-expanded="false" className="dropdown-toggle">
              <FontAwesomeIcon icon={faUserCircle} size="xl" className="mx-3" /><span>{accounts[0]?.name}</span></a>
            <ul className="collapse list-unstyled menu" id="homeSubmenu0">
              <li onClick={() => logoutHandler(instance)}>
                <NavLink to="#" >
                  <FontAwesomeIcon icon={faSignOutAlt} className="ml-4 mr-2" size="lg" /><span>Logout</span>
                </NavLink>
              </li>
            </ul>
          </li>
          <li>
            <NavLink to="/nam" className={({ isActive }) => (isActive ? 'selected' : '')}>
              <FontAwesomeIcon icon={faAddressCard} size="xl" className="mx-3" />
              <span>NAM</span>
            </NavLink>
          </li>
           <li>
            <NavLink to="/unofficial-address" className={({ isActive }) => (isActive ? 'selected unoffical' : 'unofficial')}>              
              <FontAwesomeIcon icon={faPlusSquare} size="xl" className="mx-3" />
              <span>Create Nuuday Unofficial address</span>
            </NavLink>
          </li>
          <li>
           <NavLink to="/fallout" className={({ isActive }) => (isActive ? 'selected' : '')}>
             <FontAwesomeIcon icon={faTableList} size="xl" className="mx-3" />
             <span>Fallout</span>
           </NavLink>
          </li>
          <li>
            <NavLink to="/nam-swagger" className={({ isActive }) => (isActive ? 'selected' : '')}>
              <FontAwesomeIcon icon={faBattleNet} size="xl" className="mx-3" />
              <span>Swagger</span>
            </NavLink>
          </li>
        </ul>
      </nav>
      <div id="content" className={sideEnable ? "" : "active"}>
        <div className="top-navbar">
          <nav className="navbar navbar-expand-lg">
            <div className="container-fluid">
              <button type="button" id="sidebarCollapse" className="d-xl-block d-lg-block d-md-mone d-none"
                onClick={() => setSideEnable(pre => !pre)}>
                {sideEnable ? <FontAwesomeIcon icon={faChevronLeft} size="lg" /> : <FontAwesomeIcon icon={faChevronRight} size="lg" />}
              </button>
              <Link className="navbar-brand" to="/nam"> NAM Dashboard </Link>
              <button className="d-inline-block d-lg-none ml-auto more-button" type="button" data-toggle="collapse" data-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation"
                onClick={() => setMobileEnable(true)}>
                <FontAwesomeIcon icon={faEllipsisV} size="2x" />
                
              </button>
              <div className="collapse navbar-collapse d-lg-block d-xl-block d-sm-none d-md-none d-none" id="navbarSupportedContent">
                <ul className="nav navbar-nav ml-auto">
                  <li className="dropdown nav-item">
                    <a href={window.location} className="nav-link" data-toggle="dropdown">
                      <FontAwesomeIcon icon={faUserCircle} size="2x" /> <span style={{ verticalAlign: 'super' }}>{accounts[0]?.name}</span>
                    </a>
                    <ul className="dropdown-menu">
                      <li onClick={() => logoutHandler(instance)} style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon icon={faSignOutAlt} size="lg" className="mx-3" />
                        <span>Logout</span>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleLang(i18n.language)}><FontAwesomeIcon icon={faGlobe}
              /> {i18n.language === "dk" ? "en" : "dk"}</button>
            </div>
          </nav>
        </div>
        {props.children}
      </div>
    </div>
  )
}
